// client/src/components/pages/Geofencing/GeofenceVersionHistory.js
// Version history drawer for one geofence (M1-C; contracts §2.3 / §3.2). Lists
// the immutable versions the 021 trigger recorded (who / when / what changed)
// and diffs the fields of any two versions. Read only.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaTimes, FaHistory } from 'react-icons/fa';
import ThemedSelect from '../../common/ThemedSelect';
import { formatAppDateTime } from '../../../utils/dateUtils';
import { listGeofenceVersions } from './geofenceConfigApi';
import { changedFieldLabels, diffVersions, versionAuthor, versionTime } from './geofenceConfig';

export default function GeofenceVersionHistory({ geofence, isOpen, onClose }) {
  const geofenceId = geofence?.GeofenceID;
  const [versions, setVersions] = useState([]); // newest first
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromV, setFromV] = useState(null);
  const [toV, setToV] = useState(null);

  const load = useCallback(async () => {
    if (!geofenceId) return;
    setLoading(true);
    setError(null);
    try {
      const body = await listGeofenceVersions(geofenceId);
      const list = body?.data || [];
      setAvailable(body?.available !== false);
      setVersions(list);
      setToV(list[0]?.version ?? null);
      setFromV(list[1]?.version ?? list[0]?.version ?? null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [geofenceId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const byVersion = useMemo(() => new Map(versions.map((v) => [v.version, v])), [versions]);
  const diff = useMemo(
    () => diffVersions(byVersion.get(Number(fromV)), byVersion.get(Number(toV))),
    [byVersion, fromV, toV]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Version history for ${geofence?.Name || 'zone'}`}
        className="h-full w-full max-w-lg bg-surface shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate flex items-center gap-2">
              <FaHistory className="text-gray-400 flex-shrink-0" size={16} />
              {geofence?.Name || 'Zone'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Version history
              {versions.length > 0 &&
                ` · ${versions.length} version${versions.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 transition-colors mt-1"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && <p className="text-sm text-gray-500">Loading versions…</p>}
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          {!loading && !error && !available && (
            <p className="text-sm text-gray-500">
              Version history is not available on this system yet (it arrives with the LexiStar
              geofence versioning migration).
            </p>
          )}
          {!loading && !error && available && versions.length === 0 && (
            <p className="text-sm text-gray-500">No versions recorded for this zone.</p>
          )}

          {versions.length > 1 && (
            <section aria-label="Compare versions">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Compare
              </h4>
              <div className="flex items-center gap-2">
                <div className="w-28">
                  <ThemedSelect
                    value={fromV ?? ''}
                    onChange={(e) => setFromV(Number(e.target.value))}
                    aria-label="Compare from version"
                    className="h-8 text-sm"
                  >
                    {versions.map((v) => (
                      <option key={v.version} value={v.version}>
                        {`v${v.version}`}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
                <span className="text-sm text-gray-500">→</span>
                <div className="w-28">
                  <ThemedSelect
                    value={toV ?? ''}
                    onChange={(e) => setToV(Number(e.target.value))}
                    aria-label="Compare to version"
                    className="h-8 text-sm"
                  >
                    {versions.map((v) => (
                      <option key={v.version} value={v.version}>
                        {`v${v.version}`}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
              </div>
              {diff.length === 0 ? (
                <p className="text-sm text-gray-500 mt-3">No differences between these versions.</p>
              ) : (
                <table className="mt-3 w-full text-sm" data-testid="version-diff">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="py-1 pr-3 font-medium">Field</th>
                      <th className="py-1 pr-3 font-medium">{`v${fromV}`}</th>
                      <th className="py-1 font-medium">{`v${toV}`}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 align-top">
                    {diff.map((d) => (
                      <tr key={d.key}>
                        <td className="py-1.5 pr-3 text-gray-700 font-medium whitespace-nowrap">
                          {d.label}
                        </td>
                        <td className="py-1.5 pr-3 text-gray-500 break-words">{d.before}</td>
                        <td className="py-1.5 text-gray-900 break-words">{d.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {versions.length > 0 && (
            <section aria-label="Versions">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Versions
              </h4>
              <ol className="space-y-2">
                {versions.map((v, i) => (
                  <li
                    key={v.version}
                    className="rounded-lg border border-gray-200 px-3 py-2"
                    data-testid={`version-${v.version}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-semibold text-gray-900">
                        {`v${v.version}`}
                        {i === 0 && (
                          <span className="ml-2 text-xs font-medium text-teal-700">current</span>
                        )}
                      </span>
                      <span className="text-xs text-gray-500 tabular-nums">
                        {formatAppDateTime(versionTime(v)) || '—'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">by {versionAuthor(v)}</p>
                    <p className="text-xs text-gray-700 mt-1">
                      {changedFieldLabels(versions[i + 1], v).join(', ')}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
}
